import React, { useState, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  Alert,
  CircularProgress
} from '@mui/material';
import { useForm, Controller } from 'react-hook-form';

interface OTPModalProps {
  open: boolean;
  onClose: () => void;
  onVerify: (otp: string) => Promise<void>;
  phoneNumber: string;
  refNo?: string;
  loading?: boolean;
}

interface OTPForm {
  otp: string;
}

export default function OTPModal({ 
  open, 
  onClose, 
  onVerify, 
  phoneNumber, 
  refNo,
  loading = false 
}: OTPModalProps) {
  const [error, setError] = useState<string>('');
  const [submitting, setSubmitting] = useState(false);

  const { control, handleSubmit, watch, formState: { errors }, reset } = useForm<OTPForm>({
    defaultValues: { otp: '' }
  });

  const otpValue = watch('otp');

  const onSubmit = useCallback(async (data: OTPForm) => {
    try {
      setSubmitting(true);
      setError('');
      await onVerify(data.otp);
    } catch (err: any) {
      setError(err.message || 'เกิดข้อผิดพลาดในการยืนยัน OTP');
    } finally {
      setSubmitting(false);
    }
  }, [onVerify]);

  const handleClose = () => {
    reset();
    setError('');
    onClose();
  };

  // Auto-submit when OTP is 6 digits
  React.useEffect(() => {
    if (otpValue && otpValue.length === 6) {
      handleSubmit(onSubmit)();
    }
  }, [otpValue, handleSubmit, onSubmit]);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      disableEscapeKeyDown={submitting}
    >
      <DialogTitle>
        <Typography variant="h6" component="div">
          ยืนยันตัวตน (2FA)
        </Typography>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ pt: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            กรุณากรอกรหัส OTP ที่ส่งไปยังหมายเลขโทรศัพท์
          </Typography>

          <Typography variant="body1" sx={{ mb: 2, fontWeight: 'medium' }}>
            📱 {phoneNumber}
          </Typography>

          {refNo && (
            <Typography variant="caption" color="text.secondary" sx={{ mb: 3, display: 'block' }}>
              อ้างอิง: {refNo}
            </Typography>
          )}

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Controller
            name="otp"
            control={control}
            rules={{
              required: 'กรุณากรอกรหัส OTP',
              pattern: {
                value: /^\d{6}$/,
                message: 'รหัส OTP ต้องเป็นตัวเลข 6 หลัก'
              }
            }}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                label="รหัส OTP"
                placeholder="000000"
                variant="outlined"
                type="text"
                inputProps={{
                  maxLength: 6,
                  style: { 
                    textAlign: 'center', 
                    fontSize: '1.5rem',
                    letterSpacing: '0.3em'
                  }
                }}
                error={!!errors.otp}
                helperText={errors.otp?.message}
                disabled={submitting || loading}
                autoFocus
                onChange={(e) => {
                  // Only allow digits
                  const value = e.target.value.replace(/\D/g, '');
                  field.onChange(value);
                }}
              />
            )}
          />

          <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
            รหัส OTP จะหมดอายุใน 5 นาที
          </Typography>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button 
          onClick={handleClose} 
          disabled={submitting || loading}
          color="inherit"
        >
          ยกเลิก
        </Button>
        <Button
          onClick={handleSubmit(onSubmit)}
          variant="contained"
          disabled={submitting || loading || !otpValue || otpValue.length !== 6}
          startIcon={submitting ? <CircularProgress size={20} /> : null}
        >
          {submitting ? 'กำลังยืนยัน...' : 'ยืนยัน'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
